import React from 'react';
import { Box, Typography } from '@mui/material';
import { LuCheck, LuFileText, LuTarget, LuFolder, LuEye, LuLayoutList, LuBookOpen, LuUsers } from 'react-icons/lu';
import { elliottTokens as t } from './tokens';

// Top-nav stepper — design ported from the Elliott lesson view's step stepper.
// "Requirements" covers the core program fields (name, category, audience,
// launch date, owner, purpose, tone); the rest track the Build-with-Elliott
// design steps (incl. the embedded Structure + Content stages once the wizard
// takes over inside the modal). Indices align with AIMD_STEPS.
export type StepStatus = 'complete' | 'active' | 'upcoming';

export const STEP_META: { label: string; Icon: React.ComponentType<{ size?: number; color?: string }> }[] = [
  { label: 'Requirements', Icon: LuFileText },
  { label: 'Goals', Icon: LuTarget },
  { label: 'Documents', Icon: LuFolder },
  { label: 'Preview', Icon: LuEye },
  { label: 'Structure', Icon: LuLayoutList },
  { label: 'Lessons', Icon: LuBookOpen }
];

// Animated light sweep for the active step circle.
const Shine: React.FC = () => (
  <Box
    sx={{
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      overflow: 'hidden',
      borderRadius: 'inherit',
      zIndex: 0,
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        width: '45%',
        background: 'linear-gradient(105deg, transparent 0%, rgba(255,255,255,0.35) 50%, transparent 100%)',
        transform: 'translateX(-160%)',
        animation: 'addProgShine 4s ease-in-out infinite'
      },
      '@keyframes addProgShine': {
        '0%': { transform: 'translateX(-160%)' },
        '55%, 100%': { transform: 'translateX(320%)' }
      }
    }}
  />
);

const StepNode: React.FC<{
  label: string;
  Icon: React.ComponentType<{ size?: number; color?: string }>;
  status: StepStatus;
}> = ({ label, Icon, status }) => {
  const NodeIcon = status === 'complete' ? LuCheck : Icon;
  // Complete: filled brandy circle + white tick (matches the Elliott lesson stepper).
  const iconColor = status === 'complete' ? '#FDFDFD' : t.brandLight;
  const circleSx =
    status === 'active'
      ? {
          bgcolor: 'rgba(247,148,32,0.1)',
          boxShadow: '0px 0px 16px 0px rgba(244,119,35,0.5), inset 0px 0px 8px 0px rgba(247,148,32,0.25)'
        }
      : status === 'complete'
      ? { background: t.brandyTextGradient }
      : { border: `1.5px solid ${t.borderDim}` };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          width: 36,
          height: 36,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...circleSx
        }}
      >
        {status === 'active' && <Shine />}
        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex' }}>
          <NodeIcon size={18} color={iconColor} />
        </Box>
      </Box>
      <Typography
        sx={{
          fontSize: 12,
          lineHeight: '15px',
          fontWeight: 500,
          whiteSpace: 'nowrap',
          ...(status === 'active'
            ? {
                backgroundImage: t.brandyTextGradient,
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent'
              }
            : { color: status === 'upcoming' ? t.textTertiary : t.textPrimary })
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

const StepConnector: React.FC<{ filled: boolean }> = ({ filled }) => (
  <Box sx={{ flex: 1, minWidth: 12, height: 36, display: 'flex', alignItems: 'flex-start', pt: '17px' }}>
    <Box sx={{ width: '100%', height: 2, borderRadius: 1, bgcolor: filled ? t.brandLight : t.borderDim }} />
  </Box>
);

// `showMembers` appends the Members node (index 6) — the creation-flow revamp
// routes the embedded wizard through Members before Invite & Finish.
const StepNav: React.FC<{ current: number; showMembers?: boolean }> = ({ current, showMembers = false }) => {
  const steps = showMembers ? [...STEP_META, { label: 'People', Icon: LuUsers }] : STEP_META;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        width: '100%',
        px: '24px',
        py: '14px',
        borderBottom: `1px solid ${t.borderDim}`,
        flexShrink: 0
      }}
    >
      {steps.map((s, i) => {
        const status: StepStatus = i < current ? 'complete' : i === current ? 'active' : 'upcoming';
        return (
          <React.Fragment key={s.label}>
            <StepNode label={s.label} Icon={s.Icon} status={status} />
            {i < steps.length - 1 && <StepConnector filled={i < current} />}
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export default StepNav;
